import React, { useState, useEffect } from 'react';
import { Check, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Pricing = () => {
  const [pricingData, setPricingData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPricing();
  }, []);

  const fetchPricing = async () => {
    try {
      const response = await axios.get(`${API}/pricing`);
      if (response.data && response.data.length > 0) {
        setPricingData(response.data);
      } else {
        setPricingData(defaultPricingData);
      }
    } catch (error) {
      console.error('Error fetching pricing:', error);
      setPricingData(defaultPricingData);
    } finally {
      setLoading(false);
    }
  };

  const scrollToContact = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  if (loading) {
    return (
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <div className="text-[#666666]">Loading pricing...</div>
        </div>
      </section>
    );
  }

  return (
    <section id="pricing" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 text-black">
            Services & Pricing
          </h2>
          <p className="text-base md:text-lg text-[#666666] max-w-2xl mx-auto font-light">
            Transparent pricing for premium AI-powered creative services
          </p>
        </div>

        {/* Services Grid */}
        <div className="space-y-20" style={{ overflow: 'visible' }}>
          {pricingData.map((service, serviceIndex) => (
            <div key={service.id || serviceIndex} className="space-y-8" style={{ overflow: 'visible' }}>
              {/* Service Title */}
              <div className="text-center">
                <h3 className="text-3xl sm:text-4xl font-bold text-black mb-3">
                  {service.serviceName}
                </h3>
                <div className="w-16 h-px bg-black/20 mx-auto" />
              </div>

              {/* Pricing Tiers Grid */}
              <div className={`grid gap-8 md:gap-6 lg:gap-8 ${
                service.tiers.length === 2 
                  ? 'grid-cols-1 sm:grid-cols-2 lg:max-w-4xl lg:mx-auto' 
                  : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'
              }`} style={{ paddingTop: '2rem' }}>
                {service.tiers.map((tier, tierIndex) => (
                  <div
                    key={tier.id || tierIndex}
                    className="group relative bg-white border-2 border-black/20 hover:border-black p-6 sm:p-8 pt-10 sm:pt-12 transition-all duration-300 hover:shadow-2xl hover:-translate-y-2 flex flex-col"
                    style={{ 
                      minHeight: '500px',
                      backgroundColor: tier.featured ? '#000000' : '#ffffff',
                      borderColor: tier.featured ? '#000000' : 'rgba(0,0,0,0.2)'
                    }}
                  >
                    {/* Featured Badge */}
                    {tier.featured && (
                      <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-20" style={{ whiteSpace: 'nowrap' }}>
                        <div className="bg-white text-black px-3 sm:px-4 py-1 sm:py-1.5 text-xs font-bold tracking-wider uppercase shadow-lg border border-black/10">
                          Most Popular
                        </div>
                      </div>
                    )}

                    {/* Tier Name */}
                    <div className="mb-6 relative z-10">
                      <h4
                        className="text-xl sm:text-2xl font-bold mb-2"
                        style={{ color: tier.featured ? '#ffffff' : '#000000' }}
                      >
                        {tier.tierName}
                      </h4>
                      <div
                        className="text-3xl sm:text-4xl font-bold"
                        style={{ color: tier.featured ? '#ffffff' : '#000000' }}
                      >
                        ₹{tier.price}
                      </div>
                    </div>

                    {/* Features List */}
                    <ul className="space-y-3 sm:space-y-4 mb-8 flex-grow relative z-10">
                      {tier.features.map((feature, featureIndex) => (
                        <li key={featureIndex} className="flex items-start gap-3">
                          <Check
                            className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0 mt-0.5"
                            strokeWidth={2.5}
                            style={{ color: tier.featured ? '#ffffff' : '#000000' }}
                          />
                          <span
                            className="text-xs sm:text-sm font-light leading-relaxed"
                            style={{ color: tier.featured ? 'rgba(255, 255, 255, 0.9)' : '#666666' }}
                          >
                            {feature}
                          </span>
                        </li>
                      ))}
                    </ul>

                    {/* CTA Button */}
                    <Button
                      onClick={scrollToContact}
                      className="w-full py-5 sm:py-6 text-sm sm:text-base font-medium transition-all duration-300 group mt-auto relative z-10"
                      style={{
                        backgroundColor: tier.featured ? '#ffffff' : '#000000',
                        color: tier.featured ? '#000000' : '#ffffff'
                      }}
                    >
                      <span>Start Project</span>
                      <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-16 md:mt-20 text-center px-4">
          <p className="text-sm sm:text-base text-[#666666] mb-6 font-light">
            Need a custom package? Let's discuss your requirements.
          </p>
          <Button
            onClick={scrollToContact}
            size="lg"
            variant="outline"
            className="border-2 border-black text-black hover:bg-black hover:text-white px-6 sm:px-8 py-5 sm:py-6 font-medium text-sm sm:text-base w-full sm:w-auto"
          >
            Contact Us
          </Button>
        </div>
      </div>
    </section>
  );
};

// Default pricing data as specified by user
const defaultPricingData = [
  {
    id: 'service-1',
    serviceName: 'AI Product Photoshoots',
    tiers: [
      {
        id: 'tier-1-1',
        tierName: 'Starter',
        price: '599',
        features: [
          '5 AI-generated product images',
          'Basic background selection',
          '2 revisions included',
          '48-hour delivery',
          'High-resolution files'
        ],
        featured: false
      },
      {
        id: 'tier-1-2',
        tierName: 'Pro',
        price: '899',
        features: [
          '15 AI-generated product images',
          'Custom background options',
          '5 revisions included',
          '24-hour delivery',
          'High-resolution + web-optimized files',
          'Multiple angle variations'
        ],
        featured: true
      },
      {
        id: 'tier-1-3',
        tierName: 'Brand',
        price: '1599',
        features: [
          '30 AI-generated product images',
          'Unlimited background options',
          'Unlimited revisions',
          '12-hour delivery',
          'All file formats',
          'Lifestyle scene integration',
          'Priority support'
        ],
        featured: false
      }
    ]
  },
  {
    id: 'service-2',
    serviceName: 'AI Cinematic Ads',
    tiers: [
      {
        id: 'tier-2-1',
        tierName: 'Starter',
        price: '1299',
        features: [
          '1 cinematic video ad (15-30s)',
          'AI-powered editing',
          'Stock music included',
          '3 revisions',
          'HD 1080p export',
          '72-hour delivery'
        ],
        featured: false
      },
      {
        id: 'tier-2-2',
        tierName: 'Growth',
        price: '1999',
        features: [
          '3 cinematic video ads (15-30s each)',
          'Advanced AI effects',
          'Custom music selection',
          '5 revisions per video',
          '4K export',
          '48-hour delivery',
          'Social media formats included'
        ],
        featured: true
      },
      {
        id: 'tier-2-3',
        tierName: 'Campaign',
        price: '2599',
        features: [
          '5 cinematic video ads (15-60s each)',
          'Premium AI effects & transitions',
          'Custom soundtrack',
          'Unlimited revisions',
          '4K + all social formats',
          '24-hour delivery',
          'Dedicated project manager'
        ],
        featured: false
      }
    ]
  },
  {
    id: 'service-3',
    serviceName: 'AI Model Campaigns',
    tiers: [
      {
        id: 'tier-3-1',
        tierName: 'Starter',
        price: '1299',
        features: [
          '10 AI model images',
          'Single model style',
          'Product integration',
          '3 revisions',
          'High-resolution files',
          '48-hour delivery'
        ],
        featured: false
      },
      {
        id: 'tier-3-2',
        tierName: 'Campaign',
        price: '2999',
        features: [
          '30 AI model images',
          'Multiple model styles',
          'Diverse representation',
          'Lifestyle scenes',
          'Unlimited revisions',
          'All file formats',
          '24-hour delivery',
          'Brand guidelines adherence'
        ],
        featured: true
      }
    ]
  },
  {
    id: 'service-4',
    serviceName: 'Website Design',
    tiers: [
      {
        id: 'tier-4-1',
        tierName: 'Starter',
        price: '999',
        features: [
          'Single page website',
          'Mobile responsive',
          'Contact form',
          '2 revisions',
          'Basic SEO setup',
          '7-day delivery'
        ],
        featured: false
      },
      {
        id: 'tier-4-2',
        tierName: 'Business',
        price: '1899',
        features: [
          'Up to 5 pages',
          'Mobile responsive',
          'Contact & lead forms',
          'CMS integration',
          '5 revisions',
          'Advanced SEO',
          '10-day delivery',
          '30-day support'
        ],
        featured: true
      },
      {
        id: 'tier-4-3',
        tierName: 'Brand',
        price: '3999',
        features: [
          'Up to 10 pages',
          'Full custom design',
          'Advanced animations',
          'E-commerce ready',
          'Unlimited revisions',
          'Premium SEO package',
          '14-day delivery',
          '90-day support',
          'Performance optimization'
        ],
        featured: false
      }
    ]
  }
];

export default Pricing;
